/**
 * Pipeline question-binding counts for assessment invite gating.
 *
 * Fetches existing pipeline binding endpoints in parallel. Backend still
 * enforces active-question filtering on invite creation.
 */
import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';

import { v1Get } from '@/lib/api';
import { IQ_ASSESSMENT_LABEL } from '@/lib/stageLabels';

const STAGE_LABELS = {
  VIDEOBOT: 'Video Bot',
  MCQ: IQ_ASSESSMENT_LABEL,
  TECH_ASSESSMENT: 'Tech Assessment',
};

const STAGE_WARNING =
  'No {label} questions are mapped to this pipeline. Add questions in Pipeline settings before sending an invite.';

export function usePipelineAssessmentReadiness(pipelineId) {
  const enabled = Boolean(pipelineId);

  const [vbQuery, mcqQuery, techQuery] = useQueries({
    queries: [
      {
        queryKey: ['pipeline-vb-binding', pipelineId],
        queryFn: () => v1Get(`/pipelines/${pipelineId}/videobot-questions`),
        enabled,
        staleTime: 60_000,
      },
      {
        queryKey: ['pipeline-mcq-binding', pipelineId],
        queryFn: () => v1Get(`/pipelines/${pipelineId}/mcq-questions`),
        enabled,
        staleTime: 60_000,
      },
      {
        queryKey: ['pipeline-tech-binding', pipelineId],
        queryFn: () => v1Get(`/pipelines/${pipelineId}/tech-questions`),
        enabled,
        staleTime: 60_000,
      },
    ],
  });

  const counts = useMemo(
    () => ({
      VIDEOBOT: vbQuery.data?.question_ids?.length ?? null,
      MCQ: mcqQuery.data?.question_ids?.length ?? null,
      TECH_ASSESSMENT: techQuery.data?.question_ids?.length ?? null,
    }),
    [vbQuery.data, mcqQuery.data, techQuery.data],
  );

  const isLoading = enabled && (vbQuery.isLoading || mcqQuery.isLoading || techQuery.isLoading);

  const isStageReady = (stageCode) => {
    if (!enabled) return false;
    if (isLoading) return false;
    const count = counts[stageCode];
    if (count === null) return false;
    return count > 0;
  };

  const getStageWarning = (stageCode) => {
    const count = counts[stageCode];
    if (count === null || count > 0) return null;
    const label = STAGE_LABELS[stageCode] || stageCode;
    return STAGE_WARNING.replace('{label}', label);
  };

  const getCombinedWarning = (stageCodes) => {
    const unready = (stageCodes || []).filter((code) => !isStageReady(code));
    if (!unready.length) return null;
    if (unready.length === 1) return getStageWarning(unready[0]);
    const labels = unready.map((code) => STAGE_LABELS[code] || code);
    return `No questions are mapped for: ${labels.join(', ')}. Add questions in Pipeline settings before sending an invite.`;
  };

  return {
    counts,
    isLoading,
    isStageReady,
    getStageWarning,
    getCombinedWarning,
  };
}
